import React, { useState, useEffect } from 'react';
import { X, Camera, RefreshCw, Upload, Loader2, Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/button';

const EvidenceModal = ({
    showEvidenceUpload,
    closeEvidenceModal,
    handleEvidenceUpload,
    evidenceInputRef,
    canvasRef,
    cameraMode,
    capturedImage,
    videoRef,
    stopCamera,
    capturePhoto,
    retakePhoto,
    uploadCapturedImage,
    evidenceUploading
}) => {
    const [selectedFile, setSelectedFile] = useState(null);

    useEffect(() => {
        if (!showEvidenceUpload) {
            setSelectedFile(null);
        }
    }, [showEvidenceUpload]);

    if (!showEvidenceUpload) return null;

    const handleConfirmUpload = async () => {
        if (!selectedFile) return;
        const success = await handleEvidenceUpload(selectedFile, showEvidenceUpload);
        if (success) {
            setSelectedFile(null);
            closeEvidenceModal();
        }
    };

    const handleCancel = () => {
        if (selectedFile) {
            setSelectedFile(null);
        } else {
            closeEvidenceModal();
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Attach Evidence
                    </h2>
                    <Button variant="ghost" size="sm" onClick={handleCancel}>
                        <X className="w-5 h-5" />
                    </Button>
                </div>

                <p className="text-slate-600 mb-4">
                    Upload a bill, receipt, or take a photo as evidence for this entry.
                </p>

                <input
                    ref={evidenceInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setSelectedFile(file);
                    }}
                    className="hidden"
                    data-testid="evidence-file-input"
                />

                {/* Hidden canvas for photo capture */}
                <canvas ref={canvasRef} className="hidden" />

                {/* Camera Mode */}
                {cameraMode && !capturedImage && (
                    <div className="space-y-4">
                        <div className="relative rounded-lg overflow-hidden bg-black aspect-[4/3]">
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="flex justify-center space-x-3">
                            <Button
                                onClick={stopCamera}
                                variant="outline"
                                className="border-slate-300"
                            >
                                <X className="w-4 h-4 mr-2" />
                                Cancel
                            </Button>
                            <Button
                                onClick={capturePhoto}
                                className="bg-[#003B5C] hover:bg-[#002A45] text-white"
                            >
                                <Camera className="w-4 h-4 mr-2" />
                                Capture Photo
                            </Button>
                        </div>
                    </div>
                )}

                {/* Captured Image Preview */}
                {capturedImage && (
                    <div className="space-y-4">
                        <div className="relative rounded-lg overflow-hidden bg-slate-100 aspect-[4/3]">
                            <img
                                src={URL.createObjectURL(capturedImage)}
                                alt="Captured"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="flex justify-center space-x-3">
                            <Button
                                onClick={retakePhoto}
                                variant="outline"
                                className="border-slate-300"
                                disabled={evidenceUploading}
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Retake
                            </Button>
                            <Button
                                onClick={() => uploadCapturedImage(showEvidenceUpload)}
                                disabled={evidenceUploading}
                                className="bg-[#74A57F] hover:bg-[#5F8575] text-white"
                            >
                                {evidenceUploading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4 mr-2" />
                                        Use Photo
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Selected File Preview */}
                {selectedFile && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg">
                            <div className="flex items-center space-x-3">
                                <Paperclip className="w-8 h-8 text-blue-500" />
                                <div>
                                    <p className="text-sm font-medium text-slate-700 truncate max-w-[200px]">
                                        {selectedFile.name}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedFile(null)}
                                className="text-slate-400 hover:text-red-500"
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                        <div className="flex justify-center space-x-3">
                            <Button
                                onClick={handleCancel}
                                variant="outline"
                                className="border-slate-300 w-1/2"
                                disabled={evidenceUploading}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleConfirmUpload}
                                disabled={evidenceUploading}
                                className="bg-[#003B5C] hover:bg-[#002A45] text-white w-1/2"
                            >
                                {evidenceUploading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4 mr-2" />
                                        Confirm
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Default View - Select File or Take Photo */}
                {!cameraMode && !capturedImage && !selectedFile && (
                    <>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            {/* Select File Option */}
                            <div
                                onClick={() => !evidenceUploading && evidenceInputRef.current?.click()}
                                className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#74A57F] hover:bg-[#74A57F]/5 transition-colors"
                                data-testid="select-evidence-btn"
                            >
                                {evidenceUploading ? (
                                    <div className="flex flex-col items-center">
                                        <Loader2 className="w-8 h-8 text-[#74A57F] animate-spin mb-2" />
                                        <span className="text-slate-600 text-sm">Uploading...</span>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center">
                                        <Upload className="w-8 h-8 text-slate-400 mb-2" />
                                        <span className="text-slate-700 font-medium text-sm">Select File</span>
                                        <span className="text-xs text-slate-400 mt-1">PDF, JPG, PNG</span>
                                    </div>
                                )}
                            </div>

                            {/* Take Photo Option */}
                            <div
                                onClick={() => !evidenceUploading && startCamera()}
                                className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#003B5C] hover:bg-[#003B5C]/5 transition-colors"
                                data-testid="take-photo-btn"
                            >
                                <div className="flex flex-col items-center">
                                    <Camera className="w-8 h-8 text-slate-400 mb-2" />
                                    <span className="text-slate-700 font-medium text-sm">Take Photo</span>
                                    <span className="text-xs text-slate-400 mt-1">Use camera</span>
                                </div>
                            </div>
                        </div>

                        <p className="text-xs text-slate-500 text-center mb-4">
                            Maximum file size: 10MB
                        </p>

                        <Button
                            variant="outline"
                            onClick={handleCancel}
                            className="w-full"
                        >
                            Cancel
                        </Button>
                    </>
                )}
            </div>
        </div >
    );
};

export default EvidenceModal;
